<script lang="ts">
  /**
   * The Auto section of the Scene tab: Auto show (one change at a time with the music, the whole
   * look on the big moments) and the "change one thing now" button.
   */
  import type { AppController } from '../../app/controller.svelte';
  import { SETTING_DEFS, type Settings } from '../../shared/settings';
  import Toggle from './Toggle.svelte';

  let { ctl }: { ctl: AppController } = $props();

  const s = $derived(ctl.settings);
  const optionsOf = (key: keyof Settings) => {
    const def = SETTING_DEFS.find((d) => d.key === key);
    return def && 'options' in def ? def.options : [];
  };
  const set = (key: keyof Settings, raw: string) => {
    const value = Number.isNaN(Number(raw)) ? raw : Number(raw);
    ctl.setSetting(key, value as never);
  };
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

  const elements = [
    { key: 'autoColours', label: 'Colours' },
    { key: 'autoTop', label: 'Top layers' },
    { key: 'autoBackground', label: 'Background' },
    { key: 'autoEffects', label: 'Effects' },
  ] as const;
</script>

<Toggle
  label={`Run the show automatically${ctl.keyHint('autopilot.toggle')}`}
  checked={s.autopilot}
  testid="auto-show"
  onchange={(v) => {
    if (v !== s.autopilot) ctl.toggleAutoShow();
  }}
/>
<p class="hint">
  Changes one thing at a time on the bar or the phrase (the colours, a top layer, the background,
  the effects), and the whole look on the big moments. Pinned things are always kept.
</p>

{#if s.autopilot}
  <div class="status" data-testid="auto-status" aria-live="polite">
    {#if ctl.previewMode}
      Paused while you prepare in preview.
    {:else if ctl.autoNextBars === null}
      Waiting for the music…
    {:else if ctl.autoNextBars === 0}
      Changing on the next bar…
    {:else}
      Next change in {plural(ctl.autoNextBars, 'bar')}.
    {/if}
    {#if ctl.autoLast}<span class="last">Last: {ctl.autoLast}</span>{/if}
  </div>
{/if}

<button class="next" data-testid="auto-next" onclick={() => ctl.autoNext()}
  >Change one thing now{ctl.keyHint('auto.next')}</button
>

{#if s.autopilot}
  <div class="grid">
    <label for="auto-every">Change something every</label>
    <select
      id="auto-every"
      data-testid="auto-every"
      value={String(s.autopilotBars)}
      onchange={(e) => set('autopilotBars', e.currentTarget.value)}
    >
      {#each [1, 2, 4, 8, 16, 32, 64] as b (b)}<option value={String(b)}>{plural(b, 'bar')}</option
        >{/each}
    </select>

    <label for="auto-whole">Whole new look</label>
    <select
      id="auto-whole"
      data-testid="auto-whole"
      value={s.autoWhole}
      onchange={(e) => set('autoWhole', e.currentTarget.value)}
    >
      {#each optionsOf('autoWhole') as o (o.value)}<option value={String(o.value)}>{o.label}</option
        >{/each}
    </select>

    {#if s.autoWhole !== 'never'}
      <label for="auto-from">New looks from</label>
      <select
        id="auto-from"
        data-testid="auto-from"
        value={s.autopilotPool}
        onchange={(e) => set('autopilotPool', e.currentTarget.value)}
      >
        {#each optionsOf('autopilotPool') as o (o.value)}<option value={String(o.value)}
            >{o.label}</option
          >{/each}
      </select>
      {#if s.autopilotPool !== 'generate'}
        <label for="auto-pick">Pick presets</label>
        <select
          id="auto-pick"
          value={s.autopilotSelection}
          onchange={(e) => set('autopilotSelection', e.currentTarget.value)}
        >
          {#each optionsOf('autopilotSelection') as o (o.value)}<option value={String(o.value)}
              >{o.label}</option
            >{/each}
        </select>
      {/if}
    {/if}

    <label for="auto-hold">Pause after you change something</label>
    <select
      id="auto-hold"
      value={String(s.autopilotHoldPhrases)}
      onchange={(e) => set('autopilotHoldPhrases', e.currentTarget.value)}
    >
      {#each [0, 1, 2, 4, 8] as p (p)}<option value={String(p)}
          >{p === 0 ? 'Don’t pause' : plural(p, 'phrase')}</option
        >{/each}
    </select>
  </div>

  <h4>One at a time, change the</h4>
  <div class="elements">
    {#each elements as el (el.key)}
      <Toggle
        label={el.label}
        checked={s[el.key]}
        testid={`auto-${el.key}`}
        onchange={(v) => ctl.setSetting(el.key, v)}
      />
    {/each}
  </div>

  <h4>Follow the music</h4>
  <Toggle
    label="Match visualizations, colours and pace to the energy"
    checked={s.autoFollowEnergy}
    testid="auto-follow-energy"
    onchange={(v) => ctl.setSetting('autoFollowEnergy', v)}
  />
  <Toggle
    label="React to drops, breakdowns and new sections"
    checked={s.autopilotOnSections}
    testid="auto-sections"
    onchange={(v) => ctl.setSetting('autopilotOnSections', v)}
  />
  {#if s.autopilotOnSections}
    <p class="hint">
      A drop gets {s.autoWhole === 'never' ? 'a new background' : 'a whole new look'} on the beat; a breakdown
      strips back to the background; a build-up brings in effects; a new section gets a new background.
    </p>
  {/if}
{/if}

<style>
  .status {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    align-items: baseline;
    margin: 6px 0 8px;
    padding: 7px 10px;
    border-radius: var(--radius-sm);
    background: color-mix(in srgb, var(--accent) 12%, transparent);
    font-size: 12.5px;
  }
  .last {
    color: var(--muted);
  }
  .next {
    width: 100%;
    margin: 4px 0 10px;
  }
  .grid {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 6px 10px;
    align-items: center;
    margin-bottom: 10px;
    font-size: 13px;
  }
  h4 {
    margin: 10px 0 4px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--muted);
    font-weight: 600;
  }
  .elements {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 10px;
  }
</style>
